import { useEffect, useRef, useState } from "react";
import { Button, Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchShopType } from "../actions/shopActions";
import { Product } from "../actions/types";
import { RootState } from "../reducers";
import Products from "./Products";
import ProductSkeletonCard from "./ProductSkeletonCard";
import { useParams } from "react-router-dom";
import useScrollPosition from "../hooks/useScrollPosition";
import { useNavigate } from "react-router-dom";

const ProductList = () => {
  const products = useSelector((state: RootState) => state.shop.products);
  const isLoading = useSelector((state: RootState) => state.shop.isLoading);
  const lastPage = useSelector((state: RootState) => state.shop.lastPage);
  const { productTypeId, sizeId = null }: any = useParams();
  const dispatch: any = useDispatch();
  const [page, setPage] = useState(1);
  const scrollPosition = useScrollPosition();
  const navigate: any = useNavigate();

  // Refs para detectar cambios de filtros y evitar fetch duplicado
  const prevFiltersRef = useRef<{ productTypeId: any; sizeId: any }>({
    productTypeId,
    sizeId,
  });
  const lastFetchKeyRef = useRef<string>("");
  // Evita que múltiples eventos de scroll en el mismo frame incrementen varias veces la página
  const incrementingRef = useRef(false);

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.productTypeId !== productTypeId || prev.sizeId !== sizeId;

    // Si cambiaron filtros y la página no es 1, reseteamos a 1 primero y esperamos próximo render
    if (filtersChanged && page !== 1) {
      setPage(1);
      prevFiltersRef.current = { productTypeId, sizeId };
      return;
    }

    // Actualizamos ref de filtros
    if (filtersChanged) {
      prevFiltersRef.current = { productTypeId, sizeId };
    }

    const fetchKey = `${productTypeId}|${sizeId}|${page}`;
    if (fetchKey === lastFetchKeyRef.current) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;
    dispatch(fetchShopType({ productTypeId, sizeId, page }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId, sizeId, page, dispatch]);

  useEffect(() => {
    const scrollHeight =
      (document.documentElement && document.documentElement.scrollHeight) ||
      document.body.scrollHeight;
    if (!scrollHeight) return;
    const ratio = (scrollPosition + window.innerHeight) / scrollHeight; // 0..1
    const nearThreshold = ratio >= 0.8; // 80%
    if (!isLoading && !lastPage && nearThreshold && !incrementingRef.current) {
      incrementingRef.current = true; // lock until loading ciclo termina
      setPage((p) => p + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition, isLoading, lastPage]);

  // Cuando termina la carga liberamos el lock para permitir siguiente incremento
  useEffect(() => {
    if (!isLoading) {
      incrementingRef.current = false;
    }
  }, [isLoading]);

  return (
    <Container fluid>
      <Row xs={2} md={2} lg={4} className="g-1">
        {products.map((product: Product, index: number | undefined) => (
          <Products key={index} product={product} />
        ))}
      </Row>
      {isLoading && (
        <Row xs={2} md={2} lg={4} className="g-1 mt-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Col key={`skeleton-${idx}`} className="g-2">
              <ProductSkeletonCard />
            </Col>
          ))}
        </Row>
      )}
      {!isLoading && products.length === 0 && (
        <div className="text-center mt-4">
          <h4>¡Ups! Nos quedamos sin stock de esta opción.</h4>
          <h5 className="mb-5 text-muted mt-3">Te invitamos a seguir explorando otros modelos increíbles o volver al inicio.</h5>
          <Button
            variant="secondary btn-lg mb-2"
            onClick={() => {
              navigate("/");
            }}
          >
            Inicio
          </Button>
        </div>
      )}
    </Container>
  );
};

export default ProductList;
